import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * CTO Directive #130.1 — Son Fiziksel Kabul Oturumu.
 * The 12 CTO-127 physical steps under one session_id.
 * Simulation is shown honestly and never counted as physical success.
 */
const STATUS_LABEL = {
  pending: "Denenmedi",
  passed: "Geçti",
  failed: "Geçmedi",
  skipped: "Atlandı",
  simulated: "Simülasyon",
};
const STATUS_CLASS = {
  pending: "text-slate-500 border-slate-700",
  passed: "text-emerald-400 border-emerald-700/60",
  failed: "text-rose-400 border-rose-700/60",
  skipped: "text-slate-400 border-slate-600",
  simulated: "text-amber-400 border-amber-700/60",
};
const CHANNEL_LABEL = {
  voice: "Ses", microphone: "Mikrofon", work: "Ofis",
  zekeriya: "Zekeriya", cast: "Yayın", presence: "Varlık",
};

export const AcceptanceSession = () => {
  const [session, setSession] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/pilot/acceptance/latest");
      setSession(r.data.session);
    } catch (e) { /* pilot dashboard already surfaces errors */ }
    setLoaded(true);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const start = async (platform) => {
    setBusy(true);
    try {
      const r = await api.post("/me/pilot/acceptance/start", { platform });
      setSession(r.data.session);
    } finally { setBusy(false); }
  };

  const markStep = async (step, status) => {
    setBusy(true);
    try {
      const r = await api.post(
        `/me/pilot/acceptance/${session.id}/step`, { step, status },
      );
      setSession(r.data.session);
    } finally { setBusy(false); }
  };

  const close = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/me/pilot/acceptance/${session.id}/close`);
      setSession(r.data.session);
      if (r.data.audio_base64) {
        try {
          const audio = new Audio(
            `data:${r.data.mime_type || "audio/mpeg"};base64,${r.data.audio_base64}`,
          );
          audio.play().catch(() => {});
        } catch (e) { /* written fallback already visible */ }
      }
    } finally { setBusy(false); }
  };

  const downloadReport = async () => {
    setBusy(true);
    try {
      const r = await api.get(
        `/me/pilot/acceptance/${session.id}/report.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meos-fiziksel-kabul-${session.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };

  if (!loaded) return null;

  const active = session && session.status === "active";
  const steps = session
    ? Object.values(session.steps || {}).sort((a, b) => a.step - b.step)
    : [];
  const channelCounts = {};
  (session?.linked_events || []).forEach((ev) => {
    channelCounts[ev.channel] = (channelCounts[ev.channel] || 0) + 1;
  });

  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-900/60 p-4 space-y-4" data-testid="acceptance-session">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-medium text-slate-200">
          Son Fiziksel Kabul Oturumu
        </h2>
        {session && (
          <span className="text-[11px] uppercase tracking-wide text-slate-500" data-testid="acceptance-session-status">
            {session.status === "active" ? "Aktif" : session.status === "closed" ? "Kapandı" : "Terk edildi"}
            {" · "}{session.platform === "exe" ? "Windows EXE" : "Web (simülasyon)"}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {!active && (
            <>
              <button
                data-testid="acceptance-start-exe-btn"
                disabled={busy}
                onClick={() => start("exe")}
                className="rounded-full border border-emerald-700/60 px-3 py-1 text-xs text-emerald-300 hover:bg-emerald-900/30 transition-colors"
              >
                EXE oturumu başlat
              </button>
              <button
                data-testid="acceptance-start-web-btn"
                disabled={busy}
                onClick={() => start("web")}
                className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-400 hover:bg-slate-800/60 transition-colors"
              >
                Web simülasyonu
              </button>
            </>
          )}
          {active && (
            <button
              data-testid="acceptance-close-btn"
              disabled={busy}
              onClick={close}
              className="rounded-full border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800/60 transition-colors"
            >
              Oturumu kapat
            </button>
          )}
          {session && session.status === "closed" && (
            <button
              data-testid="acceptance-report-download-btn"
              disabled={busy}
              onClick={downloadReport}
              className="rounded-full border border-sky-700/60 px-3 py-1 text-xs text-sky-300 hover:bg-sky-900/30 transition-colors"
            >
              Fiziksel kabul raporunu indir (PDF)
            </button>
          )}
        </div>
      </div>

      {!session && (
        <p className="text-sm text-slate-500" data-testid="acceptance-empty">
          Henüz fiziksel kabul oturumu yok. Gerçek donanım testi için
          Windows EXE üzerinde oturum başlatın.
        </p>
      )}

      {session && session.result && (
        <div className="space-y-1">
          {session.result.spoken_summary_tr && (
            <p className="text-sm text-slate-200" data-testid="acceptance-spoken-summary">
              {session.result.spoken_summary_tr}
            </p>
          )}
          <p
            className={`text-sm ${session.result.physically_accepted ? "text-emerald-400" : "text-amber-400"}`}
            data-testid="acceptance-result"
          >
            {session.result.summary_tr}
          </p>
        </div>
      )}

      {session && session.platform !== "exe" && (
        <p className="text-[11px] text-amber-500/90">
          Simülasyon — gerçek donanım doğrulanmadı; "Geçti" işaretleri
          fiziksel başarı sayılmaz.
        </p>
      )}

      {session && (
        <div className="space-y-1" data-testid="acceptance-steps">
          {steps.map((s) => (
            <div
              key={s.step}
              className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-sm"
              data-testid={`acceptance-step-${s.step}`}
            >
              <span className="w-5 shrink-0 text-[11px] text-slate-500">{s.step}</span>
              <span className="truncate text-slate-300">{s.title_tr}</span>
              <span
                className={`ml-auto shrink-0 rounded-full border px-2 py-0.5 text-[11px] ${STATUS_CLASS[s.status]}`}
                data-testid={`acceptance-step-${s.step}-status`}
              >
                {STATUS_LABEL[s.status] || s.status}
              </span>
              {active && (
                <span className="flex shrink-0 gap-1">
                  <button
                    data-testid={`acceptance-step-${s.step}-pass-btn`}
                    disabled={busy}
                    onClick={() => markStep(s.step, "passed")}
                    className="rounded border border-emerald-800 px-2 py-0.5 text-[11px] text-emerald-400 hover:bg-emerald-900/30"
                  >
                    Geçti
                  </button>
                  <button
                    data-testid={`acceptance-step-${s.step}-fail-btn`}
                    disabled={busy}
                    onClick={() => markStep(s.step, "failed")}
                    className="rounded border border-rose-900 px-2 py-0.5 text-[11px] text-rose-400 hover:bg-rose-900/30"
                  >
                    Geçmedi
                  </button>
                  <button
                    data-testid={`acceptance-step-${s.step}-skip-btn`}
                    disabled={busy}
                    onClick={() => markStep(s.step, "skipped")}
                    className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-400 hover:bg-slate-800/60"
                  >
                    Atla
                  </button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {session && (
        <div className="flex flex-wrap gap-2" data-testid="acceptance-channels">
          {Object.entries(CHANNEL_LABEL).map(([ch, label]) => (
            <span
              key={ch}
              className="rounded-full border border-slate-800 px-2.5 py-0.5 text-[11px] text-slate-400"
              data-testid={`acceptance-channel-${ch}`}
            >
              {label}: {channelCounts[ch] || 0}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
